/**
 * ContactJourneyLogs (PR-B, Fase 3)
 *
 * Card list of label change events for a single contact, oldest-first to
 * read like a story or newest-first toggleable. Lazy-renders incremental
 * batches (no virtualization yet — volumes <200 events on the same contact
 * are the realistic ceiling, beyond which the journey graph is the better UI).
 *
 * Source-aware icons make causality glanceable without opening a tooltip:
 *   - kanban / modal -> hand pointer
 *   - rule           -> filter (matched a label rule)
 *   - auto_dispatch  -> zap (campaign auto-add)
 *   - api            -> generic
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Loader2, RefreshCw, ArrowDownUp, MousePointerClick, Filter, Zap, Code, AlertCircle,
} from 'lucide-react';
import contactJourneyService from '../../services/global/contactJourneyService';

const SOURCE_ICONS = {
  kanban: MousePointerClick,
  modal: MousePointerClick,
  rule: Filter,
  auto_dispatch: Zap,
  api: Code,
};

const SOURCE_LABELS = {
  kanban: 'arrastar e soltar',
  modal: 'modal',
  rule: 'regra de aproximacao',
  auto_dispatch: 'auto-disparo',
  api: 'integracao',
  label_dispatch: 'disparo de etapa',
  column_deleted: 'coluna/funil excluído',
  entry_connection: 'entrada por número',
  reply_ruler: 'régua de resposta',
  message_transition: 'mensagem da conversa',
};

// Format ISO timestamp into "DD/MM HH:mm" pt-BR. We parse lazily because this
// runs N times for the list — Date constructor is cheap enough to not need
// memoization per row.
const formatDateBR = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${dd}/${mm} ${hh}h${mi}`;
  } catch {
    return iso;
  }
};

const ContactJourneyLogs = ({ phone, colors, onRequestRefresh }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [order, setOrder] = useState('desc'); // 'desc' | 'asc'

  const load = useMemo(() => async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await contactJourneyService.getLabelHistory(phone, 200);
      const data = res?.data || res;
      setEvents(data?.events || []);
    } catch (err) {
      console.error(err);
      setError('Nao foi possivel carregar o historico.');
    } finally {
      setLoading(false);
    }
  }, [phone]);

  useEffect(() => { load(); }, [load]);

  const ordered = useMemo(() => {
    if (order === 'asc') return events.slice().reverse();
    return events;
  }, [events, order]);

  const Icon = (source) => SOURCE_ICONS[source] || Code;

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 8,
      height: '100%', overflow: 'hidden',
    }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '4px 0',
      }}>
        <span style={{ fontSize: 12, color: colors.textSecondary }}>
          {events.length} eventos
        </span>
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            onClick={() => setOrder(order === 'desc' ? 'asc' : 'desc')}
            title={order === 'desc' ? 'Mostrar do mais antigo' : 'Mostrar do mais recente'}
            style={{
              display: 'flex', alignItems: 'center', gap: 4,
              padding: '4px 8px', borderRadius: 6, fontSize: 11,
              backgroundColor: 'transparent', color: colors.textSecondary,
              border: `1px solid ${colors.borderColor}`, cursor: 'pointer',
            }}
          >
            <ArrowDownUp size={11} />
            {order === 'desc' ? 'Mais recente primeiro' : 'Mais antigo primeiro'}
          </button>
          <button
            onClick={() => { load(); onRequestRefresh && onRequestRefresh(); }}
            title="Recarregar"
            style={{
              display: 'flex', alignItems: 'center', gap: 4,
              padding: '4px 8px', borderRadius: 6, fontSize: 11,
              backgroundColor: 'transparent', color: colors.textSecondary,
              border: `1px solid ${colors.borderColor}`, cursor: 'pointer',
            }}
          >
            <RefreshCw size={11} />
          </button>
        </div>
      </div>

      {/* List */}
      <div style={{
        flex: 1, overflowY: 'auto',
        display: 'flex', flexDirection: 'column', gap: 6,
        paddingRight: 4,
      }}>
        {loading ? (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 24, color: colors.textTertiary, gap: 8, fontSize: 12,
          }}>
            <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
            Carregando historico...
          </div>
        ) : error ? (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: 12, color: '#b91c1c', fontSize: 12,
            backgroundColor: '#fee2e220', borderRadius: 8,
          }}>
            <AlertCircle size={12} />
            {error}
          </div>
        ) : events.length === 0 ? (
          <div style={{
            textAlign: 'center', padding: 24, color: colors.textTertiary, fontSize: 12,
            border: `1px dashed ${colors.borderColor}`, borderRadius: 8,
          }}>
            Sem eventos para este contato.
          </div>
        ) : ordered.map((ev) => {
          const SrcIcon = Icon(ev.source);
          const isAssign = ev.event_type === 'assigned';
          return (
            <div
              key={ev.id}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                padding: 10, borderRadius: 8,
                border: `1px solid ${colors.borderColor}`,
                backgroundColor: colors.bgPrimary,
              }}
            >
              {/* Color dot from label */}
              <span style={{
                width: 10, height: 10, borderRadius: 3,
                backgroundColor: ev.label_color, marginTop: 4, flexShrink: 0,
              }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: 13, color: colors.textPrimary, fontWeight: 500,
                  display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
                }}>
                  <span style={{ color: colors.textSecondary, fontSize: 11, fontWeight: 400 }}>
                    {formatDateBR(ev.timestamp)} —
                  </span>
                  <span>
                    {isAssign ? 'entrou em' : 'saiu de'}
                    {' '}
                    <span style={{ fontWeight: 600 }}>"{ev.label_name}"</span>
                    {ev.pipeline_name && (
                      <span style={{ color: colors.textSecondary, fontWeight: 400 }}>
                        {' '}· funil <span style={{ fontWeight: 600 }}>{ev.pipeline_name}</span>
                      </span>
                    )}
                    {ev.label_deleted && (
                      <span style={{ color: colors.textTertiary, fontStyle: 'italic', fontSize: 11 }}> (removida)</span>
                    )}
                  </span>
                </div>
                <div style={{
                  marginTop: 4, fontSize: 11, color: colors.textTertiary,
                  display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap',
                }}>
                  <SrcIcon size={11} />
                  via {SOURCE_LABELS[ev.source] || ev.source}
                  {ev.message_excerpt && (
                    <>
                      <span>•</span>
                      <span style={{ fontStyle: 'italic' }}>"{ev.message_excerpt}"</span>
                    </>
                  )}
                </div>
                {/* Data real da venda/perda informada no modal (outcome_at). */}
                {ev.outcome_at && (
                  <div style={{ marginTop: 4, fontSize: 11, color: colors.textSecondary, paddingLeft: 8 }}>
                    <strong style={{ color: colors.textPrimary }}>
                      {ev.outcome === 'won' ? 'Data da venda' : 'Data da perda'}:
                    </strong>{' '}
                    {new Date(ev.outcome_at).toLocaleDateString('pt-BR')}
                  </div>
                )}
                {/* Motivo do fechamento, quando a etapa era de ganho/perda.
                    Só aparece se o operador preencheu (pular grava null). */}
                {ev.outcome_reason && (
                  <div style={{
                    marginTop: 5, fontSize: 11, lineHeight: 1.45,
                    color: colors.textSecondary, paddingLeft: 8,
                    borderLeft: `2px solid ${
                      ev.outcome === 'won' ? colors.accentPrimary : colors.danger
                    }`,
                  }}>
                    <strong style={{ color: colors.textPrimary }}>
                      {ev.outcome === 'won' ? 'Motivo do ganho' : 'Motivo da perda'}:
                    </strong>{' '}
                    {ev.outcome_reason}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ContactJourneyLogs;
